import { openapiClient, unwrapResult } from '@/api/openapi-client';
import { useRestaurantStore } from '@/stores/restaurant.store';
import type {
  Franchise,
  FranchiseUpdateBody,
  Restaurant,
  RestaurantUpdateBody,
} from '@restaurant/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

const RESTAURANT_PATH = '/api/restaurants/{restaurantId}';
const FRANCHISE_PATH = '/api/franchises/{franchiseId}';

function useRestaurantId() {
  return useRestaurantStore((s) => s.selectedRestaurantId!);
}

// ── Basic info ─────────────────────────────────────────────────────────────

export function useRestaurant() {
  const restaurantId = useRestaurantId();
  return useQuery({
    queryKey: ['restaurant', restaurantId],
    queryFn: async () =>
      unwrapResult<Restaurant>(
        await openapiClient.GET(RESTAURANT_PATH, { params: { path: { restaurantId } } }),
      ),
    enabled: !!restaurantId,
  });
}

export function useUpdateRestaurant() {
  const restaurantId = useRestaurantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: RestaurantUpdateBody) =>
      unwrapResult<Restaurant>(
        await openapiClient.PATCH(RESTAURANT_PATH, { params: { path: { restaurantId } }, body }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['restaurant', restaurantId] }),
  });
}

// ── Franchise (venue-level: default language) ───────────────────────────────
// `defaultLanguage` lives on the Franchise, not the Restaurant. The business
// profile page reads it here (via the restaurant's franchiseId) and the picker
// saves it via updateFranchise. Owner-scoped on the backend.

export function useFranchise(franchiseId: string | undefined) {
  return useQuery({
    queryKey: ['franchise', franchiseId],
    queryFn: async () => {
      if (!franchiseId) throw new Error('franchiseId is required.');
      return unwrapResult<Franchise>(
        await openapiClient.GET(FRANCHISE_PATH, { params: { path: { franchiseId } } }),
      );
    },
    enabled: !!franchiseId,
  });
}

export function useUpdateFranchise(franchiseId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: FranchiseUpdateBody) => {
      if (!franchiseId) throw new Error('franchiseId is required.');
      return unwrapResult<Franchise>(
        await openapiClient.PATCH(FRANCHISE_PATH, { params: { path: { franchiseId } }, body }),
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['franchise', franchiseId] });
    },
  });
}
